import React, { useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchCheckinSalesDetailPdf } from '@/apis/aipass'
import { toPaymentMethodInputs, usePaymentMethods, validatePaymentMethods } from '@/hooks/use-payment-methods'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Button, ButtonType } from '@/components/atoms/button'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { TextInputWithDropdown } from '@/components/molecules/text-input-with-dropdown'
import { useReceiptProviso } from '@/hooks/use-receipt-proviso'
import { Checkbox } from '@/components/atoms/checkbox'

type ReceiptPopupProps = {
  onClose: () => void
  customer: { id: string; guest: { name: string; company?: string } }
  checkinId?: string
  reservationId?: string
  filterGuestRoomAssignId?: string[]
  totalAmount: number
}

export const ReceiptPopup: React.FC<ReceiptPopupProps> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const customerName = props.customer?.guest?.name
  const customerCompany = props.customer?.guest?.company
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [name, setName] = useState(customerName ? customerName : '')

  const { provisoOptions, defaultProvision, isLoading: isProvisoLoading, cacheProvision } = useReceiptProviso()
  const [provision, setProvision] = useState<string>('')

  const [outputLanguage, setOutputLanguage] = useState('ja')
  const { paymentMethods, setField, isMultipleChecked } = usePaymentMethods()
  const [paymentTouched, setPaymentTouched] = useState(false)

  const isPaymentInvalid = !!validatePaymentMethods(paymentMethods, props.totalAmount)
  const paymentErrorKey = paymentTouched ? validatePaymentMethods(paymentMethods, props.totalAmount) : null

  useEffect(() => {
    setProvision(prev => prev || defaultProvision)
  }, [defaultProvision])

  const downloadSalesPdf = async () => {
    setIsLoading(true)
    const checkedInputs = toPaymentMethodInputs(paymentMethods)
    const params = {
      reservationId: props.reservationId,
      filterGuestRoomAssignId: props.filterGuestRoomAssignId,
      checkinId: props.checkinId,
      guestName: name,
      provision,
      isEnOutput: outputLanguage === 'en' ? 1 : 0,
      paymentMethods: checkedInputs.length > 0 ? checkedInputs : undefined,
    }
    try {
      const { url } = await fetchCheckinSalesDetailPdf(params)
      window.open(url, '_blank')
      props.onClose()
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
      setDisabled(false)
    }
  }

  const onSaveData = () => {
    setDisabled(true)
    cacheProvision(provision)
    downloadSalesPdf()
  }

  return (
    <React.Fragment>
      <div css={modalWrapperStyle}>
        <div css={modalContentStyle}>
          <div css={modalTitleWrapperStyle}>
            <p css={modalTitleStyle}>{t('Receipt issue')}</p>
            <RadioGroupField
              itemCss={radioItemStyle}
              value={outputLanguage}
              style={{ labelMargin: 12 }}
              items={[
                { value: 'ja', label: t('Japanese language') },
                { value: 'en', label: 'English' },
              ]}
              onChange={e => {
                setOutputLanguage(e.target.value)
              }}
            />
          </div>
          <div css={mainContentStyle}>
            <div css={inputWrapperStyle}>
              <span css={titleInputStyle}>{t('Your name')}</span>
              {customerCompany ? (
                <TextInputWithDropdown
                  value={name}
                  onChange={setName}
                  options={[
                    { value: customerName, label: customerName },
                    { value: customerCompany, label: customerCompany },
                  ]}
                  placeholder="山田 太郎"
                />
              ) : (
                <input
                  name="text"
                  type="text"
                  css={nameInputStyle}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="山田 太郎"
                />
              )}
            </div>
            <div css={inputWrapperStyle}>
              <span css={titleInputStyle}>{t('Proviso')}</span>
              <TextInputWithDropdown
                value={provision}
                onChange={setProvision}
                options={provisoOptions}
                placeholder={t('As accommodation fee')}
              />
            </div>
            <div css={inputWrapperStyle}>
              <span css={[titleInputStyle, { alignSelf: 'flex-start', paddingTop: 6 }]}>{t('ReceiptPayment.PaymentMethod')}</span>
              <div>
                <div css={paymentMethodsGridStyle}>
                  {/* 現金 */}
                  <Checkbox
                    style={paymentCheckboxSpanStyle}
                    value={paymentMethods.cash.checked}
                    label={t('ReceiptPayment.Cash')}
                    onChange={v => setField('cash', 'checked', v)}
                  />
                  <input
                    type="text"
                    css={amountInputStyle}
                    value={paymentMethods.cash.amount}
                    style={{ visibility: paymentMethods.cash.checked && isMultipleChecked ? 'visible' : 'hidden' }}
                    onChange={e => setField('cash', 'amount', e.target.value)}
                    onBlur={() => setPaymentTouched(true)}
                    placeholder="1000"
                  />

                  {/* クレジットカード */}
                  <Checkbox
                    style={paymentCheckboxSpanStyle}
                    value={paymentMethods.creditCard.checked}
                    label={t('ReceiptPayment.CreditCard')}
                    onChange={v => setField('creditCard', 'checked', v)}
                  />

                  <input
                    type="text"
                    css={amountInputStyle}
                    value={paymentMethods.creditCard.amount}
                    style={{ visibility: paymentMethods.creditCard.checked && isMultipleChecked ? 'visible' : 'hidden' }}
                    onChange={e => setField('creditCard', 'amount', e.target.value)}
                    onBlur={() => setPaymentTouched(true)}
                    placeholder="1000"
                  />

                  {/* その他 */}
                  <Checkbox value={paymentMethods.other.checked} onChange={v => setField('other', 'checked', v)} />
                  <input
                    type="text"
                    css={[otherNameInputStyle, !paymentMethods.other.checked && otherNameDisabledStyle]}
                    value={paymentMethods.other.name}
                    disabled={!paymentMethods.other.checked}
                    onChange={e => setField('other', 'name', e.target.value)}
                    onBlur={() => setPaymentTouched(true)}
                  />
                  <input
                    type="text"
                    css={amountInputStyle}
                    value={paymentMethods.other.amount}
                    style={{ visibility: paymentMethods.other.checked && isMultipleChecked ? 'visible' : 'hidden' }}
                    onChange={e => setField('other', 'amount', e.target.value)}
                    onBlur={() => setPaymentTouched(true)}
                    placeholder="1000"
                  />
                </div>
                {paymentErrorKey && <p css={paymentErrorStyle}>{t(paymentErrorKey)}</p>}
              </div>
            </div>
          </div>
          <div>
            <div css={buttomButtonStyle}>
              <Button
                width={110}
                height={38}
                buttonType={ButtonType.Secondary}
                fontSize={14}
                marginRight={16}
                fontWeight={'normal'}
                onClick={() => props.onClose()}
              >
                {t('Cancel')}
              </Button>
              <Button
                width={126}
                fontWeight={'normal'}
                height={38}
                buttonType={disabled || isPaymentInvalid ? ButtonType.PrimaryDisabled : ButtonType.Primary}
                fontSize={14}
                isDisabled={disabled || isPaymentInvalid}
                onClick={() => onSaveData()}
              >
                {t('Download')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <LoadingFull isLoading={isLoading || isProvisoLoading} />
    </React.Fragment>
  )
}

// Modal
const modalWrapperStyle = css({
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'scroll',
})
const mainContentStyle = css({
  marginTop: 60,
  background: '#F2F2F2 0% 0% no-repeat padding-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 16,
  padding: '24px 32px',
})

const inputWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
})

const titleInputStyle = css({
  fontSize: 12,
  color: '#676767',
  letterSpacing: 0.6,
  width: 66,
  textAlign: 'left',
})

const inputStyle = css({
  height: 32,
  padding: '8px 16px',
  border: '1px solid #ccc',
  borderRadius: 16,
  textAlign: 'left',
  appearance: 'none',
  '::placeholder': {
    color: '#ccc',
  },
})

const nameInputStyle = css(inputStyle, {
  width: 302,
})

const provisoInputStyle = css(nameInputStyle, {
  border: 'none',
})

const provisoComboBoxStyle = css({
  width: 310,
  '& .ant-select': { height: '34px !important' },
  '& .ant-select-selection': { borderRadius: '16px !important', borderColor: '#CCCCCC !important' },
  '& .ant-select-selection__rendered': { height: '32px !important', fontSize: '16px !important' },
})

const modalContentStyle = css({
  width: 680,
  background: '#fff',
  borderRadius: 5,
  marginBottom: 40,
})

const modalTitleWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  width: 'inherit',
  paddingInline: 32,
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '5px 5px 0 0',
})

const modalTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  lineHeight: '60px',
  letterSpacing: '0.9px',
})

const buttomButtonStyle = css({
  display: 'flex',
  padding: '11px 32px',
  justifyContent: 'flex-end',
  backgroundColor: '#FFFFFF',
  borderRadius: '0px 0px 5px 5px',
})

const radioItemStyle = css({
  paddingLeft: 12,
  fontSize: 12,
  label: {
    color: '#676767',
  },
  img: {
    width: 18,
    height: 18,
  },
})

const paymentMethodsGridStyle = css({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 140px',
  rowGap: 5,
  columnGap: 5,
  alignItems: 'baseline',
  marginLeft: 10,
})

// 現金・クレジットカード行はCheckbox（icon+label）で列1-2をspan
const paymentCheckboxSpanStyle = css({
  gridColumn: '1 / 3',
})

const amountInputStyle = css({
  height: 32,
  padding: '0 16px',
  border: '1px solid #ccc',
  borderRadius: 16,
  width: 140,
  appearance: 'none',
  '::placeholder': { color: '#ccc' },
})

const otherNameInputStyle = css({
  height: 32,
  padding: '0 16px',
  border: '1px solid #ccc',
  borderRadius: 16,
  appearance: 'none',
  '::placeholder': { color: '#ccc' },
})

const otherNameDisabledStyle = css({
  backgroundColor: '#f2f2f2',
  color: '#ccc',
})

const paymentErrorStyle = css({
  fontSize: 11,
  color: '#E84828',
  marginTop: 4,
})
